/* eslint-disable react/jsx-props-no-spreading */
import * as z from 'zod';

import { Button, FormControl, Input, Stack, Text } from '@chakra-ui/react';
import { SubmitHandler, useForm } from 'react-hook-form';

import PreAuthService from '@/client/services/api/PreAuthService';
import { useConfigStore } from '@/client/services/state/configStore';
import useDocumentTitle from '../utils/useDocumentTitle';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

export default function ForgotPassword() {
  useDocumentTitle('Password Recover');
  const { t } = useTranslation();
  const { config } = useConfigStore();
  const domain = config.host;
  const { setToast } = useToastStore();

  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  interface ResetPasswordMutationVariables {
    email: string;
  }

  const resetPassword = useMutation<void, Error, ResetPasswordMutationVariables>({
    mutationFn: ({ email }) => PreAuthService.requestPasswordReset(email, domain),
    onSettled: () => {
      setShowSuccess(true);

      setToast({
        show: true,
        status: 'success',
        title: t('preauth.forgot.emailHasBeenSent'),
      });
    },
  });

  const forgotPasswordFormSchema = z.object({
    email: z.string().email(),
  });

  const {
    handleSubmit,
    register,

    formState: { errors, isValid },
  } = useForm<z.infer<typeof forgotPasswordFormSchema>>({
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: {
      email: '',
    },
    mode: 'all',
  });

  const onSubmit: SubmitHandler<z.infer<typeof forgotPasswordFormSchema>> = (data) =>
    resetPassword.mutateAsync({ email: data.email });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4} width="100%">
        {!showSuccess && (
          <>
            <Text fontSize={{ base: '24px', md: '36px' }} fontWeight={700}>
              {t('preauth.forgot.headline')}
            </Text>

            <FormControl isInvalid={!!errors.email}>
              <Input
                {...register('email')}
                name="email"
                type="email"
                placeholder={`${t('global.forms.labels_emailAddress')}`}
                variant="neutral"
              />

              {errors?.email && (
                <Text fontSize="14px" fontWeight={500} color="error">
                  {t('login.email.error')}
                </Text>
              )}
            </FormControl>

            <Button isDisabled={!isValid} type="submit">
              {t('preauth.forgot.button')}
            </Button>
          </>
        )}

        {showSuccess && (
          <>
            <Text fontSize={{ base: '24px', md: '36px' }} fontWeight={700}>
              {t('preauth.forgot.successHeadline')}
            </Text>

            <Text fontSize="14px" fontWeight={500} color="neutral.800">
              {t('preauth.forgot.successHint')}
            </Text>
          </>
        )}
      </Stack>
    </form>
  );
}
